import { useState } from "react";
import { MessageCircle, Loader2, User, Phone, Mail } from "lucide-react";

// Visitor pre-chat / lead-capture form. Fields shown + required are driven by
// the tenant's widget config (config.pre_chat).
export function PreChatForm({ config, accent, starting, onStart }) {
  const pc = config.pre_chat || {};
  const enabled = pc.enabled;
  const show = enabled
    ? { name: pc.name, phone: pc.phone, email: pc.email }
    : { name: true, phone: false, email: false };
  const req = enabled
    ? { name: pc.name_required, phone: pc.phone_required, email: pc.email_required }
    : {};

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");

  const submit = () => {
    if (show.name && req.name && !name.trim()) return setErr("Please enter your name.");
    if (show.phone && req.phone && !phone.trim()) return setErr("Please enter your phone number.");
    if (show.email && req.email && !email.trim()) return setErr("Please enter your email.");
    if (show.email && email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setErr("Please enter a valid email.");
    setErr("");
    onStart({ name: name.trim(), phone: phone.trim(), email: email.trim() });
  };

  const inputCls = "w-full rounded-2xl border border-black/10 bg-[#f6f7f8] py-2.5 pl-9 pr-3.5 text-[15px] text-[#111b18] outline-none transition-colors focus:border-[#0e8f7e] focus:bg-white";

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 overflow-y-auto px-6 py-8 text-center" data-testid="widget-prechat-form">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-lg" style={{ background: accent }}>
        <MessageCircle className="h-8 w-8" aria-hidden="true" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-[#111b18]">{config.display_name}</h3>
        <p className="mt-1 text-sm text-[#667781]">
          {enabled ? "Share a few details and we'll get right back to you." : "Start a conversation with our team."}
        </p>
      </div>
      <div className="w-full max-w-xs space-y-2.5">
        {show.name && (
          <div className="relative">
            <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a9691]" aria-hidden="true" />
            <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder={"Your name" + (req.name ? " *" : "")} className={inputCls} data-testid="widget-name-input" />
          </div>
        )}
        {show.phone && (
          <div className="relative">
            <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a9691]" aria-hidden="true" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} type="tel" inputMode="tel" placeholder={"Phone number" + (req.phone ? " *" : "")} className={inputCls} data-testid="widget-phone-input" />
          </div>
        )}
        {show.email && (
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a9691]" aria-hidden="true" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} type="email" placeholder={"Email address" + (req.email ? " *" : "")} className={inputCls} data-testid="widget-email-input" />
          </div>
        )}
        {err && <p className="text-left text-xs font-medium text-red-500" data-testid="widget-prechat-error">{err}</p>}
        <button
          onClick={submit}
          disabled={starting}
          className="flex w-full items-center justify-center gap-2 rounded-2xl py-2.5 text-sm font-semibold text-white shadow-md transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
          style={{ background: accent }}
          data-testid="widget-start-chat-button"
        >
          {starting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <MessageCircle className="h-4 w-4" aria-hidden="true" />}
          Start chat
        </button>
      </div>
    </div>
  );
}
